import { CboRole, MultiselectOption } from '@cbo/shared-library';
import { capitalize } from '@mui/material/utils';
import { UserPreferences } from '@cbo/shared-library/request/admin.request';
import dayjs from 'dayjs';
import { GridRenderCellParams, GridRowParams, GridValidRowModel } from '@mui/x-data-grid-premium';
import { User } from '../models/User';
import { CategoryData } from './models/Category';
import { InvoiceStatusAction } from './models/Invoice';
import isPermitted from '../lib/permissions';
import { GroupedOption } from '../components/DataGrid/CustomCells/CustomEditableGroupedSelectCell';
import { GlAccount } from '../../api/sales/requests';
import {
  CustomUnit,
  IndustryUnit,
  InventoryUnitsData,
  RecipeUnit,
  VendorInventoryUnitsMap,
} from './models/UnitsOfMeasure';
import { MobileSize } from '../admin/DashboardPage/ScorecardContainer/ScorecardContainer';
import { RecipesFiltersType } from './models/Recipe';

export interface IAutocompleteOption {
  inputValue?: string;
  label: string;
  inputProps: { 'data-testid': string; id: string };
}

export interface UnitOptions {
  id: string;
  unitname: string | undefined;
}

export enum UnitClass {
  COUNT = 'Count',
  VOLUME = 'Colume',
  WEIGHT = 'Weight',
}

export const preventInvoiceStatusChange = (
  newStatus: InvoiceStatusAction,
  draftVendorOrItems: boolean,
  user: User
): boolean => {
  if (!draftVendorOrItems) return false; // no draft vendor or items are tied to this invoice, so no need to prevent the change

  if (newStatus === InvoiceStatusAction.Accepted && !isPermitted(user as User, [CboRole.INVOICE_ACCEPT_NEW])) {
    return true;
  }

  if (newStatus === InvoiceStatusAction.Finalized && !isPermitted(user as User, [CboRole.INVOICE_FINALIZE_NEW])) {
    return true;
  }

  return false;
};

export const isValidThreeDecimal = (value: number | undefined | null) => {
  if (value === undefined || value === null) return true;
  return /^\d+(\.\d{1,3})?$/.test(value.toString());
};

export const isValidNumber = (value: string | undefined | null) => {
  if (value === undefined || value === null) return true;
  return /^-?\d+(\.\d+)?$/.test(value);
};

export function determineOptionLabelValue(newValue: IAutocompleteOption | string) {
  if (typeof newValue === 'string') {
    return newValue;
  }
  if (newValue.inputValue) {
    return newValue.inputValue;
  }
  return newValue.label;
}

export const flattenCategory = (category: CategoryData): Omit<CategoryData, 'subAccounts'>[] => {
  const categoryWithoutSubAccounts = { ...category };
  delete categoryWithoutSubAccounts.subAccounts;

  if (category.subAccounts) {
    return [categoryWithoutSubAccounts, ...category.subAccounts.flatMap(flattenCategory)];
  }

  return [categoryWithoutSubAccounts];
};

export const flattenGlAccountCategory = (glAccount: GlAccount): GlAccount[] => {
  const glAccountWithoutSubAccounts = { ...glAccount };
  glAccountWithoutSubAccounts.subAccounts = [];

  if (glAccount && glAccount.subAccounts) {
    return [glAccountWithoutSubAccounts, ...glAccount.subAccounts.flatMap(flattenGlAccountCategory)];
  }
  return [glAccountWithoutSubAccounts];
};

// This will be hardcoded in starter until it is pointed at the custom GL service
export const OMIT_PARENT_SERVICE_ACCOUNT = ['6000', '6200', '6300', '7000', '7100', '7500'];

export const STARTER_SERVICE_ITEMS = [
  '6300',
  '6310',
  '6330',
  '6350',
  '6360',
  '7100',
  '7120',
  '7140',
  '7145',
  '7155',
  '7190',
  '7195',
  '7199',
  '7200',
  '7300',
  '7400',
  '7500',
  '7524',
  '7600',
  '8100',
];

export const getCategoryLabel = (category: CategoryData | undefined) =>
  category ? `${category.accountNumber} - ${category.accountName}` : '';

export const groupCategories = (category: CategoryData): GroupedOption[] => {
  const groupedOptions: GroupedOption[] = [];

  if (category.subAccounts && category.subAccounts.length > 0) {
    const headerNameSplit = category.accountName.split(' ');
    // Ex: headerNameSplit = ['Total', '___', 'cost];
    const headerName = headerNameSplit.slice(1, headerNameSplit.length - 1).join();
    groupedOptions.push({
      label: capitalize(headerName),
      value: '',
      isSubheader: true,
    });

    groupedOptions.push(
      ...category.subAccounts.map((subAccount) => ({
        label: getCategoryLabel(subAccount),
        value: String(subAccount.accountNumber),
        isSubheader: false,
      }))
    );
  }

  return groupedOptions;
};

export const getReceivingUnitsFromMap = (
  inventoryUnitsVendorItemData: VendorInventoryUnitsMap | undefined
): Map<string, RecipeUnit> => {
  const receivingUnitsMap = new Map<string, RecipeUnit>();
  if (inventoryUnitsVendorItemData != null) {
    // convert to proper map
    let map = new Map<string, InventoryUnitsData[]>();
    if (inventoryUnitsVendorItemData.items.size === undefined) {
      Object.entries(inventoryUnitsVendorItemData.items).forEach(([key, values]) => {
        map.set(key, values);
      });
    } else {
      map = inventoryUnitsVendorItemData.items;
    }

    // create a map with receiving unitId and UnitName,class,type
    // only include the top level container unit
    Array.from(map).forEach(([key, values]) => {
      values.forEach((item: { unitId: string; unitClass: string; unitType: string }) => {
        if (key.includes('/') || key.includes('=')) {
          if (!receivingUnitsMap.has(item.unitId)) {
            receivingUnitsMap.set(item.unitId, {
              class: item.unitClass,
              name: key,
              id: item.unitId,
            });
          } else if (
            !Array.from(receivingUnitsMap.values()).some((unit) => unit.name.includes('/') && unit.name.includes('='))
          ) {
            receivingUnitsMap.set(item.unitId, {
              class: item.unitClass,
              name: key,
              id: item.unitId,
            });
          } else if (!Array.from(receivingUnitsMap.values()).some((unit) => unit.name.includes('='))) {
            receivingUnitsMap.set(item.unitId, {
              class: item.unitClass,
              name: key,
              id: item.unitId,
            });
          }
        }
      });
    });
  }
  return receivingUnitsMap;
};

export const getChipColor = (value: unknown) => {
  switch (value) {
    case 'Accepted':
      return 'success';
    case 'Draft':
      return 'warning';
    default:
      return 'primary';
  }
};

export const getKpiCardsProps = (isLoading: boolean, isMobile: boolean, preferences: UserPreferences | null) => ({
  loading: isLoading,
  width: isMobile ? '100%' : 'calc((100% - 16px)/3)',
  height: '300px',
  mobileSize: 'small' as MobileSize,
  preferences,
});

export const getUnitName = (unitid: string, unitOptions: UnitOptions[]) => {
  if (unitid && unitOptions.length > 0) {
    const uom = unitOptions.filter((unit) => unit.id === unitid)[0];
    return uom?.unitname;
  }
  return '';
};

export const getUnitOptions = (industryUnits: IndustryUnit[] | CustomUnit[]) =>
  industryUnits?.map((unit: IndustryUnit | CustomUnit) => ({
    id: unit.id,
    unitname: unit.name,
  })) ?? [];

export const getSelectUnitOptions = (industryUnits: IndustryUnit[], filterArray?: string[]) =>
  (filterArray && filterArray.length > 0
    ? industryUnits.filter((unit) => unit.name && filterArray.includes(unit.name))
    : industryUnits
  ).map((unit: IndustryUnit) => ({
    label: unit.name,
    value: unit.id,
  })) ?? [];

export const isSameOrAfterStartDate = (startDate: Date | undefined | null, endDate: Date | undefined | null) => {
  if (!endDate || !startDate) {
    return true;
  }

  const startDateWithoutTime = dayjs(startDate).startOf('day');
  const endDateWithoutTime = dayjs(endDate).startOf('day');

  return endDateWithoutTime.isSame(startDateWithoutTime) || endDateWithoutTime.isAfter(startDateWithoutTime);
};

/**
 * Determines if a vendor item row is assigned to a different raw item or is used in an invoice for the current raw item.
 * Returns an object with:
 * - isAssignedToOtherRawItem: true if the row's rawItemId exists and does not match the provided rawItemId.
 * - isUsedInInvoiceForCurrentRawItem: true if the row's rawItemId matches the provided rawItemId and is used in an invoice.
 */
export const checkIsVendorItemMappedOrUsedInInvoice = (
  params: GridRowParams | GridRenderCellParams<GridValidRowModel>,
  rawItemId: string | undefined
) => {
  const { rawItemId: rowRawItemId, usedInInvoice } = params.row;
  const isAssignedToOtherRawItem = rowRawItemId && rowRawItemId !== rawItemId;
  const isUsedInInvoiceForCurrentRawItem = params.row.rawItemId === rawItemId && usedInInvoice;

  return { isAssignedToOtherRawItem, isUsedInInvoiceForCurrentRawItem };
};

export const transformRecipeFilters = (filters: RecipesFiltersType) => {
  const keyNames: Record<string, string> = {
    name: 'name',
    id: 'recipeId',
    type: 'isPrepItem',
    status: 'isActive',
    containsPrepItem: 'containsPrepItem',
    yieldUnit: 'unitId',
    ingredient: 'ingredientName',
    yieldAmount: 'recipeMakes',
  };

  const getLabelValue = (key: string, item: MultiselectOption) => {
    switch (key) {
      case 'type':
        return item.label === 'Recipe' ? 'false' : 'true';
      case 'containsPrepItem':
        return item.label === 'Yes' ? 'true' : 'false';
      case 'status':
        return item.label === 'Active' ? 'true' : 'false';
      case 'yieldUnit':
        return item.id;
      default:
        return item.label;
    }
  };

  return Object.entries(filters).flatMap(([key, value]) => {
    const keyName = keyNames[key];
    if (!keyName) return [];

    if (Array.isArray(value)) {
      return value.map((item) => `${keyName}:eq:${getLabelValue(key, item)}`);
    }

    if (keyName === 'recipeMakes' && value.min && value.max) {
      const conditions: string[] = [];
      conditions.push(`${keyName}:gte:${value.min}`);
      conditions.push(`${keyName}:lte:${value.max}`);
      return conditions;
    }

    return [];
  });
};
